import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AccommodationRoomForm } from "Bands/components/AccommodationRoomForm/AccommodationRoomForm";
import { clearActionMeta, dataMetaSelector } from "Bands/state";
import {
  createAccommodationRoomType,
  dispatchGetEventAccommodationRoomTypes,
} from "Bands/state/data/actions/eventBlocks/eventAccommodationRoomType";
import { BAND_ACTIONS, IAccommodationRoomTypeParams } from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Hotel } from "shared/assets/icons";
import { Modal } from "shared/components";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { API_ENDPOINTS } from "shared/constants";
import { addTemporaryToast } from "shared/components/Toast";
import { dispatchGetEventAccommodationRoomsForBlock } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoom";

const { accommodationBlock: blockCopy } = copy;

interface Props {
  accommodationBlockId: string;
  handleClose: () => void;
}

export const CreateAccommodationRoomModal: React.FC<Props> = ({
  accommodationBlockId,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const [addAnother, setAddAnother] = useState(false);
  const [globalInfo, setGlobalWarning] = useState<string>();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.createAccommodationRoomType));

  useClearActionMetaOnUnMount(BAND_ACTIONS.createAccommodationRoomType);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.createAccommodationRoomType));

      dispatchGetEventAccommodationRoomTypes(dispatch, accommodationBlockId);

      if (!addAnother) {
        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: blockCopy.create.roomsBlock.messages.success,
          })
        );
        handleClose();

        dispatchGetEventAccommodationRoomsForBlock(dispatch, accommodationBlockId);
      }

      setGlobalWarning(blockCopy.create.roomsBlock.messages.success);
      setTimeout(() => setGlobalWarning(""), 2000);

      setAddAnother(false);
    }
  }, [apiMeta?.status, dispatch, handleClose, accommodationBlockId, addAnother]);

  const handleSubmit = (data: IAccommodationRoomTypeParams) => {
    dispatch(
      createAccommodationRoomType({
        accommodationBlock: API_ENDPOINTS.EVENT_ACCOMMODATION_BLOCK(accommodationBlockId),
        ...data,
      })
    );
  };

  const createAndAddAnother = () => {
    setAddAnother(true);
  };

  const buttons: IFormButtons = {
    primary: {
      children: blockCopy.create.roomsBlock.fields.title,
      "data-testid": "add_accommodation_room_submit",
    },
    secondary: {
      children: blockCopy.create.roomsBlock.fields.createAnother,
      onClick: createAndAddAnother,
      isSubmitButton: true,
    },
  };

  return (
    <Modal
      title={blockCopy.create.roomsBlock.fields.title}
      icon={Hotel}
      showCloseButton={true}
      handleClose={handleClose}
    >
      <AccommodationRoomForm
        buttons={buttons}
        handleSubmit={handleSubmit}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiError={apiMeta?.error}
        error={!!apiMeta?.error}
        globalInfo={globalInfo}
        minimumQuantity={1}
        minimumPeoplePerRoom={1}
      />
    </Modal>
  );
};
